import useIsMounted from "../../../../../../hooks/use-is-mounted";
import React, {useCallback, useEffect, useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import Api from "../../../../../../../core/services/api/api";
import {Card, MenuItem, Typography} from "@mui/material";
import Form, {MuiRFF, Yup, useForm, useFormV2} from "../../../../../../components/form";
import Grid2 from "@mui/material/Unstable_Grid2";
import Button from "../../../../../../components/base/button/button";
import ExportingProgress from "../exporting-progress";
import CategoryHierarchyMultiSelect, {resolveLeafCategoryIds} from "../../../../../../components/app-specific/category-hierarchy-multi-select";
import useIndustry from "../../../../../../hooks/use-industry";

const visibilityFilterValues = {
    all: 'all',
    publicForBuyers: 'publicForBuyers',
    privateOnly: 'privateOnly',
};

/**
 * Builds ExportParts filter payload from form values, expanding selected categories to leaf ids.
 * @param {*} values
 * @param {{id: number, parentId?: number|null}[]} categoriesFlat
 */
const prepareApiFilters = (values, categoriesFlat = []) => {
    const selectedCategoryIds = Array.isArray(values.categories) ? values.categories : [];
    const leafCategoryIds = resolveLeafCategoryIds(selectedCategoryIds, categoriesFlat);
    const prefix = values.prefix?.trim();

    let isPublicForBuyers = null;
    switch (values.visibility) {
        case visibilityFilterValues.publicForBuyers:
            isPublicForBuyers = true;
            break;
        case visibilityFilterValues.privateOnly:
            isPublicForBuyers = false;
            break;
        default:
            break;
    }

    return {
        categoryIds: leafCategoryIds.length ? leafCategoryIds : undefined,
        prefix: prefix?.length ? prefix : null,
        isPublicForBuyers: isPublicForBuyers,
        sorts: [],
    };
};

/**
 * Form body for products basic information export filters.
 * Loads industry categories, clears category selection on industry change, and resolves leaves on submit.
 */
const ProductsBasicInformationFormFields = ({
    exporting,
    progress,
    industries,
    fetchIndustry,
    categoriesOfFetchedIndustry,
    loadingFetchedCategories,
    required,
    visibilityOptions,
    t,
}) => {
    const form = useForm();
    const {values} = useFormV2() ?? {values: {}};
    const previousIndustryIdRef = useRef(values?.industryId);

    /**
     * Fetches categories whenever the selected industry changes.
     */
    useEffect(() => {
        if (!values?.industryId) {
            return;
        }
        void fetchIndustry({industryId: values.industryId});
    }, [fetchIndustry, values?.industryId]);

    /**
     * Clears selected categories when the industry changes after the initial mount value.
     */
    useEffect(() => {
        const previousIndustryId = previousIndustryIdRef.current;
        if (previousIndustryId !== values?.industryId) {
            if (previousIndustryId != null) {
                form?.change('categories', []);
            }
            previousIndustryIdRef.current = values?.industryId;
        }
    }, [form, values?.industryId]);

    return (
        <>
            <Grid2 container spacing={3} justifyContent={'center'}>
                <Grid2 xs={12} sx={{display: 'flex'}}>
                    <Typography variant={'subtitle1'} align={'center'}>
                        {t('formTitle')}
                    </Typography>
                </Grid2>
                <Grid2 xs={12} md={8}>
                    <MuiRFF.TextField
                        name="prefix"
                        label={t('prefixLabel')}
                        placeholder={t('prefixPlaceholder')}
                        fullWidth
                        required={required.prefix}
                        disabled={exporting}
                        size={'small'}
                    />
                </Grid2>
                <Grid2 xs={12} md={4}>
                    <MuiRFF.TextField
                        select
                        name="visibility"
                        label={t('visibilityLabel')}
                        fullWidth
                        required
                        disabled={exporting}
                        size={'small'}
                        SelectProps={{
                            renderValue: (value) => visibilityOptions.find((option) => option.value === value)?.label,
                        }}
                    >
                        {
                            visibilityOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))
                        }
                    </MuiRFF.TextField>
                </Grid2>
                <Grid2 xs={12}>
                    <MuiRFF.TextField
                        select
                        name={'industryId'}
                        size={'small'}
                        label={t('industryLabel')}
                        fullWidth
                        SelectProps={{renderValue: (value) => industries.find(e => e.id === value)?.title ?? ""}}
                    >
                        {
                            industries.map(industry => (
                                <MenuItem key={industry.id} value={industry.id}>
                                    {industry.title}
                                </MenuItem>
                            ))
                        }
                    </MuiRFF.TextField>
                </Grid2>
                {
                    values?.industryId &&
                    <Grid2 xs={12}>
                        <CategoryHierarchyMultiSelect
                            form
                            name="categories"
                            categories={categoriesOfFetchedIndustry}
                            loading={loadingFetchedCategories}
                            disabled={exporting}
                            label={t('categoriesLabel')}
                            helperText={t('categoriesHelper')}
                        />
                    </Grid2>
                }
                <Grid2 xs={12} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBlockStart: 2}}>
                    <Button
                        size={"small"}
                        disabled={exporting}
                        variant={'outlined'}
                        color={'primary'}
                        type={'submit'}
                        sx={{minWidth: 200}}
                    >
                        {exporting ? t('exporting') : t('export')}
                    </Button>
                </Grid2>
            </Grid2>
            {
                exporting &&
                <ExportingProgress progress={progress}/>
            }
        </>
    );
};

export const ExportDataViewProductsBasicInformationForm = ({fetchExportDataWithPagination, exportReport, exporting, progress}) => {
    const isMounted = useIsMounted();
    const {
        industries,
        cachedIndustry,
        fetchIndustry,
        fetchedIndustry,
        categoriesOfFetchedIndustry,
        loadingFetchedCategories,
    } = useIndustry();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.settings.export.productsBasicInformation'});

    const schema = useMemo(() => Yup.object().shape({
        industryId: Yup.string().nullable(),
        categories: Yup.array().of(Yup.number()).nullable().optional(),
        prefix: Yup.string().trim().nullable().optional(),
        visibility: Yup.string().required(),
    }), []);

    const initialValues = useMemo(() => ({
        industryId: fetchedIndustry?.id ?? cachedIndustry?.id,
        categories: [],
        prefix: '',
        visibility: visibilityFilterValues.all,
    }), [cachedIndustry?.id, fetchedIndustry?.id]);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const visibilityOptions = useMemo(() => [
        {label: t('visibilityOptions.all'), value: visibilityFilterValues.all},
        {label: t('visibilityOptions.publicForBuyers'), value: visibilityFilterValues.publicForBuyers},
        {label: t('visibilityOptions.privateOnly'), value: visibilityFilterValues.privateOnly},
    ], [t]);

    /**
     * Automatically fetches the industry if there is only one industry available and it's not already fetched or selected
     */
    useEffect(() => {
        if (industries?.length === 1) {
            const industry = industries[0];
            void fetchIndustry({industryId: industry.id});
        }
    }, [fetchIndustry, industries]);

    /**
     * Fetches a page of data from the API and prepares it for export, including determining if there are more pages to fetch.
     * @type {function(*, *): Promise<{data, hasNext: *, total: *}>}
     */
    const fetchAndPrepareData = useCallback(async (filters, pagination) => {
        const response = await Api.exportParts({
            ...filters,
            pagination,
        });
        if (!isMounted()) {
            return;
        }
        const total = response?.data?.totalCount ?? 0;
        const hasNext = response?.data?.hasNextPage ?? (total > 0 && total > pagination.pageSize * pagination.currentPage);

        return {
            data: response?.data?.items?.map((entry, index) => {
                const partId = entry.partId;
                const partNumber = entry.partNumber;
                const shortDescription = entry.shortDescription;
                const description = entry.description;

                return {
                    "": (index + 1) + ((pagination.currentPage - 1) * pagination.pageSize),
                    [t('csvColumns.partId')]: partId ?? '',
                    [t('csvColumns.partNumber')]: partNumber != null && partNumber !== '' ? `="${partNumber}"` : '',
                    [t('csvColumns.shortDescription')]: shortDescription ?? '',
                    [t('csvColumns.description')]: description ?? '',
                };
            }) ?? [],
            hasNext,
            total,
        };
    }, [isMounted, t]);

    /**
     * Submits the export form after resolving selected categories to leaf ids.
     * @type {(function(*): Promise<void>)}
     */
    const submitForm = useCallback(async (values) => {
        const filters = prepareApiFilters(values, categoriesOfFetchedIndustry);
        await exportReport(async () => {
            const data = await fetchExportDataWithPagination(filters, fetchAndPrepareData, 100);
            return [data, t('exportFileTitle')];
        });
    }, [categoriesOfFetchedIndustry, exportReport, fetchAndPrepareData, fetchExportDataWithPagination, t]);

    return (
        <Card variant={'outlined'} sx={{padding: 3, marginTop: 2}}>
            <Form
                onSubmit={submitForm}
                validate={validate}
                initialValues={initialValues}
                formProps={{style: {width: '100%'}}}
            >
                <ProductsBasicInformationFormFields
                    exporting={exporting}
                    progress={progress}
                    industries={industries}
                    fetchIndustry={fetchIndustry}
                    categoriesOfFetchedIndustry={categoriesOfFetchedIndustry}
                    loadingFetchedCategories={loadingFetchedCategories}
                    required={required}
                    visibilityOptions={visibilityOptions}
                    t={t}
                />
            </Form>
        </Card>
    );
};
